import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { Provider } from 'react-redux';
import { msalInstance } from "./services/entraAuth";
import { store } from './store';
import './App.css';
import { ErrorBoundary, SkeletonLoader } from './components/ui';
import { Layout } from './components/layout';
import ScrollToTop from './components/ui/ScrollToTop';

// Lazy load pages for code splitting
const HomePage = React.lazy(() => import('./pages/HomePage'));
const GTMPage = React.lazy(() => import('./pages/GTMPage'));
const AllSolutionsPage = React.lazy(() => import('./pages/AllSolutionsPage'));
const SolutionDetailPage = React.lazy(() => import('./pages/SolutionDetailPage'));
const AllContentPage = React.lazy(() => import('./pages/AllContentPage'));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'));
const AgentDetailPage = React.lazy(() => import('./pages/AgentDetailPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const LinkPreviewTestPage = React.lazy(() => import('./pages/LinkPreviewTestPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50">
    <SkeletonLoader />
  </div>
);

const AppContent = () => {
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  // Handle redirect response from Entra ID
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        await instance.handleRedirectPromise();
      } catch (error) {
        console.error("Redirect error:", error);
      }
    };
    handleRedirect();
  }, [instance]);

  // Auto-login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && inProgress === "none" && accounts.length === 0) {
      instance.loginRedirect({
        scopes: ["openid", "profile", "email", "User.Read"],
        redirectUri: process.env.REACT_APP_REDIRECT_URI,
      });
    }
  }, [isAuthenticated, instance, inProgress, accounts]);

  if (inProgress !== "none") {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Router>
          <ScrollToTop />
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={
                  <ErrorBoundary>
                    <HomePage />
                  </ErrorBoundary>
                } />
                <Route path="/gtm/:id" element={
                  <ErrorBoundary>
                    <GTMPage />
                  </ErrorBoundary>
                } />
                <Route path="/solutions" element={
                  <ErrorBoundary>
                    <AllSolutionsPage />
                  </ErrorBoundary>
                } />
                <Route path="/solutions/:id" element={
                  <ErrorBoundary>
                    <SolutionDetailPage />
                  </ErrorBoundary>
                } />
                <Route path="/content" element={
                  <ErrorBoundary>
                    <AllContentPage />
                  </ErrorBoundary>
                } />
                <Route path="/agents" element={
                  <ErrorBoundary>
                    <AgentsPage />
                  </ErrorBoundary>
                } />
                <Route path="/agents/:id" element={
                  <ErrorBoundary>
                    <AgentDetailPage />
                  </ErrorBoundary>
                } />

                <Route path="/contact" element={
                  <ErrorBoundary>
                    <div className="py-12 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Contact Page</h1>
                        <p className="text-gray-600">Coming Soon</p>
                      </div>
                    </div>
                  </ErrorBoundary>
                } />
                <Route path="/link-preview-test" element={
                  <ErrorBoundary>
                    <LinkPreviewTestPage />
                  </ErrorBoundary>
                } />
                {/* 404 Route */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </Layout>
        </Router>
      </ErrorBoundary>
    </HelmetProvider>
  );
};

function App() {
  return (
    <Provider store={store}>
      <MsalProvider instance={msalInstance}>
        <AppContent />
      </MsalProvider>
    </Provider>
  );
}

export default App;
